import { useUIStore } from '@/config/stores/ui-store';
import { chatEE } from '@/layout/events/chat-event-emitter';
import { Authorize } from '@/shared/components/authorize';
import { toastCommingSoon } from '@/shared/utilities/comming-soon';
import { AdminPanelSettings, Check, ChevronRight, Delete, HowToReg, Person2, Send, Settings } from '@mui/icons-material';
import { CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationRole, type Member, isHigherOrEqualRole } from '../../models/member';
import { PERMISSION_KEYS } from '../../models/permission';
import { useLeaveConversation } from '../../queries/conversation.mutations';
import { useMemberDelete } from '../../queries/member.mutations';
import { useAssignMemberRole, useTransferOwnership } from '../../queries/permission.mutations';

interface ConversationMemberMenuProps {
  contextMenu: { mouseX: number; mouseY: number } | null;
  selectedMember: Member | null;
  curUser: Member;
  convId: string;
  onClose: () => void;
}

export const ConversationMemberMenu: FC<ConversationMemberMenuProps> = ({
  convId,
  contextMenu,
  selectedMember,
  curUser,
  onClose
}) => {
  const { t } = useTranslation();
  const { xoa } = useMemberDelete(convId);
  const { leave } = useLeaveConversation();
  const { activeConversation } = useUIStore();

  // Role assignment mutation
  const { mutateAsync: assignRole, isPending: isAssigning } = useAssignMemberRole(convId);
  const { mutateAsync: transfer, isPending: isTransfering } = useTransferOwnership(convId);

  // Submenu anchor for role selection
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuAction = async (action: string) => {
    if (!selectedMember) return;

    switch (action) {
      case 'viewProfile':
        toastCommingSoon();
        break;
      case 'sendMessage':
        if (!selectedMember.user) return;
        chatEE.emit('conversation-create-direct', {
          user: selectedMember.user
        });
        break;
      case 'removeMember':
        await xoa(selectedMember.userId);
        break;
      case 'leaveGroup':
        await leave(convId);
        break;
    }
    onClose();
  };

  // Handle role change from submenu
  const handleRoleChange = async (role: ConversationRole) => {
    if (!selectedMember) return;
    await assignRole({ targetUserId: selectedMember.userId, role });
    setRoleMenuAnchor(null);
    onClose();
  };

  // Close role submenu when main menu closes
  const handleMainMenuClose = () => {
    setRoleMenuAnchor(null);
    onClose();
  };

  const isGroup = activeConversation?.conversationType === 'GROUP';
  const isSelf = selectedMember?.userId === curUser?.userId;

  if (contextMenu === null) return <></>;
  return (
    <>
      <Menu
        open
        onClose={handleMainMenuClose}
        onContextMenu={(event) => event.preventDefault()}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {!isSelf && (
          <MenuItem onClick={() => handleMenuAction('viewProfile')}>
            <ListItemIcon>
              <Person2 />
            </ListItemIcon>
            <ListItemText>{t('conversations.member.viewProfile')}</ListItemText>
          </MenuItem>
        )}
        {isGroup && !isSelf && (
          <MenuItem onClick={() => handleMenuAction('sendMessage')}>
            <ListItemIcon>
              <Send />
            </ListItemIcon>
            <ListItemText>{t('conversations.member.sendMessage')}</ListItemText>
          </MenuItem>
        )}

        <Authorize
          conversationId={convId}
          hiddenIf={isSelf || isHigherOrEqualRole(selectedMember?.role, curUser.role)}
          permissions={[PERMISSION_KEYS.ROLE_ASSIGN]}
        >
          {/* Chuyển role */}
          <MenuItem onClick={(e) => setRoleMenuAnchor(e.currentTarget)} disabled={isAssigning}>
            <ListItemIcon>
              {isAssigning || isTransfering ? <CircularProgress size={20} /> : <AdminPanelSettings />}
            </ListItemIcon>
            <ListItemText>{t('conversations.member.changeRole')}</ListItemText>
            <ChevronRight fontSize='small' sx={{ ml: 1 }} />
          </MenuItem>

          {/* Ghi đè quyền */}
          <MenuItem
            onClick={() => {
              if (selectedMember) {
                const member: Member = selectedMember;
                chatEE.emit('permission-config-open', { convId, member });
                onClose();
              }
            }}
          >
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText>{t('conversations.member.configurePermissions')}</ListItemText>
          </MenuItem>
        </Authorize>

        {/* Chuyển quyền sở hữu */}
        <Authorize conversationId={convId} permissions={[PERMISSION_KEYS.TRANSFER_OWNERSHIP]} hiddenIf={isSelf}>
          <MenuItem onClick={() => transfer(selectedMember!.userId!)} disabled={isTransfering} sx={{ color: 'warning.main' }}>
            <ListItemIcon>
              {isTransfering ? <CircularProgress size={20} /> : <HowToReg color='warning' />}
            </ListItemIcon>
            <ListItemText>{t('conversations.member.transferOwner')}</ListItemText>
          </MenuItem>
        </Authorize>

        {/* Xoá thành viên khỏi nhóm */}
        <Authorize
          conversationId={convId}
          permissions={[PERMISSION_KEYS.MEMBER_REMOVE]}
          hiddenIf={isSelf || selectedMember?.role === ConversationRole.OWNER}
        >
          <MenuItem onClick={() => handleMenuAction('removeMember')} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Delete color='error' />
            </ListItemIcon>
            <ListItemText>{t('conversations.member.removeMember')}</ListItemText>
          </MenuItem>
        </Authorize>
      </Menu>

      {/* Role Selection Submenu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={() => setRoleMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {[ConversationRole.ADMIN, ConversationRole.MODERATOR, ConversationRole.MEMBER].map((role) => {
          const isCurrentRole = selectedMember?.role === role;
          return (
            <MenuItem key={role} onClick={() => handleRoleChange(role)} disabled={isCurrentRole || isAssigning}>
              <ListItemText>{t(`conversations.members.${role.toLowerCase()}`)}</ListItemText>
              {isCurrentRole && <Check fontSize='small' sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
